import React, { useEffect, useState } from "react";
import { useRefreshRead } from "@/hooks/useRefreshRead";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINRFull } from "@/lib/format";
import { LedgerTable } from "@/components/v3/LedgerTable";
import { SourceBadge } from "@/components/v3/SourceBadge";
import { LoadingState, EmptyState } from "@/components/v3/States";

export default function BookV3() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  const [loadError, setLoadError] = useState(false);
  const load = () => api.get("/v3/book/v3").then((r) => { setRows(r.data.rows); setLoadError(false); }).catch(() => setLoadError(true));
  useEffect(() => { load(); }, []);
  useRefreshRead(load);
  if (loadError) return <div data-testid="v3-book-error" role="alert" className="p-6 text-red-700 text-sm">Loan records could not be refreshed. <button data-testid="v3-book-retry" onClick={load} className="rf-link">Retry</button></div>;
  return (
    <div className="p-6 lg:p-8 max-w-[1300px]" data-testid="book-v3-page">
      <header className="mb-6">
        <div className="ceo-label !text-[#b3122a]">Operate · Build 6</div>
        <h1 className="ceo-fig text-3xl text-[#111111] mt-1">Loan Book V3</h1>
        <p className="text-[13px] text-slate-500 mt-1.5">Sanctioned & disbursed V3 test loans with co-lending splits. Historical facilities remain in the legacy Loan Book; the unified merge arrives in Build 7.</p>
      </header>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="book-v3-table">
        {rows === null ? <LoadingState /> : rows.length === 0 ? <EmptyState title="No sanctioned or disbursed V3 loans yet" /> : (
          <div className="overflow-x-auto">
            <LedgerTable data-testid="v3-book-ledger" labels={["Loan", "Borrower", "Facility", "Disbursed", "Tranches", "Partner", "Partner / RF", "Rate band", "Status", "Source"]} className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="px-4 py-2.5">Loan</th><th className="px-4 py-2.5">Borrower</th><th className="px-4 py-2.5 text-right">Facility</th>
                <th className="px-4 py-2.5 text-right">Disbursed</th><th className="px-4 py-2.5 text-right">Tranches</th>
                <th className="px-4 py-2.5">Partner</th><th className="px-4 py-2.5 text-right">Partner / RF split</th>
                <th className="px-4 py-2.5">Rate band</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Source</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.v3_id} data-testid={`book-v3-row-${r.v3_id}`} onClick={() => navigate(`/loan/${r.v3_id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-2.5 font-mono text-[12px] text-slate-500">{r.loan_id}</td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{r.company}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`book-facility-${r.v3_id}`}>{formatINRFull(r.facility)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-bold" data-testid={`book-disbursed-${r.v3_id}`}>{formatINRFull(r.disbursed)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{r.tranches}</td>
                    <td className="px-4 py-2.5 text-slate-600">{r.partner || "—"}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[12px]" data-testid={`book-funding-${r.v3_id}`}>{r.partner ? `${formatINRFull(r.partner_funding)} / ${formatINRFull(r.redfort_funding)}` : "—"}</td>
                    <td className="px-4 py-2.5 text-[12px]">{r.rate_band || "—"}</td>
                    <td className="px-4 py-2.5 text-[11px] font-bold uppercase text-slate-500">{r.disbursement_status}</td>
                    <td className="px-4 py-2.5"><SourceBadge source="TEST_V3" /></td>
                  </tr>
                ))}
              </tbody>
            </LedgerTable>
          </div>
        )}
      </div>
    </div>
  );
}
